import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Loader2, FileText, User, History } from 'lucide-react';
import { ParseResponse } from './types';
import { OrderParserHeader, OrderInput } from './components';
import { useAuth } from '@/shared/contexts';
import { DownloadAppModal } from '@/shared/components/ui';

const API_URL = import.meta.env.VITE_API_URL || 'https://dev.api.giotheapp.com';

const TokenRestaurantParser: React.FC = () => {
  const { accessToken, isLoading: isAuthLoading } = useAuth();
  const navigate = useNavigate();

  // Get URL params
  const urlParams = new URLSearchParams(window.location.search);
  const tokenFromUrl = urlParams.get('token');

  const getToken = useCallback(() => {
    return tokenFromUrl || accessToken || '';
  }, [tokenFromUrl, accessToken]);

  const [orderText, setOrderText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<'parse' | 'success'>('parse');
  const [initialized, setInitialized] = useState(false);

  // Modal state
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [modalTitle, setModalTitle] = useState('Please Download the App');

  // Initialize - fetch restaurants
  useEffect(() => {
    if (isAuthLoading || initialized) return;

    // const fetchData = async () => {
    //     const token = getToken();
    //     if (!token) return;

    //     try {
    //         const restUrl = supplierIdFromUrl
    //             ? `${API_URL}/api/users/supplierRestaurantList?supplierId=${supplierIdFromUrl}`
    //             : `${API_URL}/api/users/supplierRestaurantList`;

    //         const restResp = await axios.get<{ data: Restaurant[] }>(restUrl, {
    //             headers: { Authorization: `Bearer ${token}` }
    //         });
    //         setRestaurants(restResp.data.data);

    //         if (restResp.data.data.length === 1) {
    //             setSelectedRestaurant(restResp.data.data[0]);
    //             setStep('parse');
    //         }
    //     } catch (err) {
    //         console.error('Failed to fetch data:', err);
    //         setError('Failed to load data');
    //     }
    // };

    // fetchData();
    setInitialized(true);
  }, [isAuthLoading, getToken, initialized]);

  const handleParse = async () => {
    const trimmed = orderText.trim();
    if (!trimmed) {
      setError('Please enter some order text first');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const requestBody = {
        text: trimmed
      };

      const response = await axios.post<ParseResponse>(
        `${API_URL}/api/orders/parse-order`,
        requestBody,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getToken()}`
          }
        }
      );

      // Navigate to results page with data
      navigate('/restaurant-order-results', {
        state: {
          result: response.data,
          editableItems: response.data.items,
          selectedRestaurant: null,
          token: tokenFromUrl
        }
      });
    } catch (err: unknown) {
      console.error('Parse error:', err);
      const message = axios.isAxiosError<{ message?: string }>(err)
        ? err.response?.data?.message
        : undefined;
      setError(message || 'Failed to parse order');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setOrderText('');
    setError(null);
    setStep('parse');
  };

  // Show loading
  if (isAuthLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#006B82]" />
      </div>
    );
  }

  // Show auth error
  // if (!isAuthenticated && !tokenFromUrl) {
  //     return (
  //         <div className="light min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 flex flex-col">
  //             <OrderParserHeader step='parse' selectedRestaurant={null} onNewOrder={() => { }} />
  //             <main className="flex-1 flex items-center justify-center p-4">
  //                 <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
  //                     <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
  //                         <AlertCircle className="w-8 h-8 text-red-600" />
  //                     </div>
  //                     <h2 className="text-2xl font-bold text-gray-900 mb-2">Authentication Required</h2>
  //                     <p className="text-gray-600 mb-6">Please log in.</p>
  //                     <a href="/login" className="inline-block px-6 py-3 bg-[#006B82] text-white rounded-xl hover:bg-[#005A6E]">
  //                         Log In
  //                     </a>
  //                 </div>
  //             </main>
  //         </div>
  //     );
  // }

  return (
    <div className="light min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 flex flex-col">
      <div className="bg-gradient-to-r from-[#006B82] to-[#00809C] text-white px-3 sm:px-6 py-3 flex justify-between items-center">
        <OrderParserHeader
          step={step === 'success' ? 'success' : 'parse'}
          selectedRestaurant={null}
          onNewOrder={handleClear}
        />
        <div className="flex items-center gap-2 sm:gap-3">
          <button
            onClick={() => {
              setModalTitle('Order History');
              setShowDownloadModal(true);
            }}
            className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition text-sm font-medium"
          >
            <History className="w-4 h-4" />
            <span className="hidden sm:inline">Order History</span>
          </button>
          <button
            onClick={() => {
              setModalTitle('Invoices');
              setShowDownloadModal(true);
            }}
            className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition text-sm font-medium"
          >
            <FileText className="w-4 h-4" />
            <span className="hidden sm:inline">Invoices</span>
          </button>
          <button
            onClick={() => {
              setModalTitle('Profile');
              setShowDownloadModal(true);
            }}
            className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition text-sm font-medium"
          >
            <User className="w-4 h-4" />
            <span className="hidden sm:inline">Profile</span>
          </button>
        </div>
      </div>

      {step === 'parse' && (
        <main className="flex-1 flex flex-col items-center md:flex-row px-3 sm:px-4 md:px-6 pt-2 md:py-8 gap-4 md:gap-6 lg:gap-8">
          <div className="w-full max-w-4xl mx-auto px-2 sm:px-6">
            {/* {renderRestaurantDropdown()} */}
            <OrderInput
              orderText={orderText}
              onOrderTextChange={setOrderText}
              onParse={handleParse}
              onClear={handleClear}
              loading={loading}
              error={error}
            />
          </div>
        </main>
      )}

      {/* Download App Modal */}
      <DownloadAppModal
        isOpen={showDownloadModal}
        onClose={() => setShowDownloadModal(false)}
        title={modalTitle}
      />
    </div>
  );
};

export default TokenRestaurantParser;
